import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Checkbox,
  makeStyles,
  MessageBar,
  MessageBarBody,
  shorthands,
  Spinner,
  tokens,
} from '@fluentui/react-components';
import { Shell, BackFooter } from '../components/Shell';
import { Steps, Title, Muted } from '../components/ui';
import { postJson, ApiError } from '../lib/api';
import { useSignup } from '../lib/signupState';
import { content } from '../content/content';

interface PermissionsDoc {
  placeholder?: boolean;
  permissions: { name: string; reason: string }[];
}

interface TermsDoc {
  placeholder?: boolean;
  version: string;
  effective: string;
  title: string;
  sections: { heading: string; paragraphs: string[] }[];
}

const useStyles = makeStyles({
  terms: {
    maxHeight: '46dvh',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    padding: '14px 16px',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('6px'),
    backgroundColor: tokens.colorNeutralBackground3,
    fontSize: tokens.fontSizeBase300,
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}` },
  },
  termsTitle: { margin: '0 0 4px', fontSize: '1.05rem', color: 'var(--tellec-heading)' },
  meta: { margin: '0 0 8px', color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  h3: { margin: '14px 0 4px', fontSize: tokens.fontSizeBase300, color: 'var(--tellec-heading)' },
  p: { margin: '0 0 8px' },
  fullControl: {
    margin: 0,
    padding: '10px 12px',
    borderLeft: `3px solid ${tokens.colorCompoundBrandStroke}`,
    backgroundColor: tokens.colorNeutralBackground3,
    fontSize: tokens.fontSizeBase300,
  },
  agree: {
    padding: '10px',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('6px'),
    backgroundColor: tokens.colorNeutralBackground3,
    '& label': { fontWeight: tokens.fontWeightSemibold },
  },
  error: { margin: 0, color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 },
  actions: { display: 'grid', gap: '12px' },
  primary: { minHeight: '48px', fontSize: tokens.fontSizeBase400 },
  consent: { margin: 0, paddingLeft: '20px', display: 'grid', gap: '6px', fontSize: tokens.fontSizeBase300 },
  code: { fontFamily: tokens.fontFamilyMonospace, fontSize: '0.92em', overflowWrap: 'anywhere' },
  permsHeader: { '& button': { paddingLeft: 0, fontSize: tokens.fontSizeBase300 } },
  perms: { listStyleType: 'none', margin: 0, padding: 0, display: 'grid', gap: '10px' },
  permName: { display: 'block', fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200, color: 'var(--tellec-heading)', overflowWrap: 'anywhere' },
  permReason: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2 },
  linkButton: { justifyContent: 'flex-start', textAlign: 'left', paddingLeft: 0, paddingRight: 0, whiteSpace: 'normal', height: 'auto' },
});

export function Terms() {
  const s = useStyles();
  const navigate = useNavigate();
  const { state, update } = useSignup();
  const [terms, setTerms] = useState<TermsDoc | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [agreeError, setAgreeError] = useState('');
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState<'consent' | 'admin' | null>(null);
  const [perms, setPerms] = useState<PermissionsDoc | null>(null);

  // Terms live in /terms/nfr-terms.json so the page, the stored acceptance
  // and the emailed PDF all use the same wording and version.
  useEffect(() => {
    fetch('/terms/nfr-terms.json', { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(setTerms)
      .catch(() => setLoadFailed(true));
    fetch('/config/graph-permissions.json', { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(setPerms)
      .catch(() => setPerms(null));
  }, []);

  const details = state.details;
  if (!details) return <Navigate to="/details" replace />;

  // Accept the terms once per session; both buttons reuse the same signup.
  async function ensureSignup(): Promise<string> {
    if (state.signupId && state.signupFor === details!.email) return state.signupId;
    const { signupId } = await postJson<{ signupId: string }>('/api/signup', {
      ...details,
      t: state.token,
      msIdToken: state.msIdToken,
      termsAccepted: true,
      termsVersion: terms!.version,
    });
    update({ signupId, signupFor: details!.email });
    return signupId;
  }

  async function go(kind: 'consent' | 'admin') {
    setFormError('');
    if (!terms) {
      setAgreeError('Wait for the terms to load.');
      return;
    }
    if (!agreed) {
      setAgreeError('Tick the box to agree to the NFR terms.');
      return;
    }
    setBusy(kind);
    try {
      const id = await ensureSignup();
      if (kind === 'consent') {
        window.location.assign(`/api/consent/start?id=${encodeURIComponent(id)}`);
      } else {
        navigate(`/done?status=admin&id=${encodeURIComponent(id)}`);
      }
    } catch (err) {
      setBusy(null);
      const e = err as ApiError;
      if (e.field === 't') {
        // Bad or expired personalised link: carry on as a walk-up.
        update({ token: undefined });
        setFormError(`${e.message} Press the button again to continue without it.`);
        return;
      }
      if (e.field && e.field !== 'termsAccepted') {
        // A details field was rejected server-side (e.g. blocked domain): send them back to fix it.
        update({ serverError: { field: e.field, message: e.message } });
        navigate('/details');
        return;
      }
      setFormError(e.message);
    }
  }

  return (
    <Shell
      labelledBy="title"
      brandTitle="The fine print"
      brandTagline="The NFR licence covers your own tenant only. We'll email you a PDF copy of these terms."
      footerLeft={<BackFooter to="/details" />}
    >
      <Steps step={2} of={3} />
      <Title id="title">NFR licence terms</Title>

      {terms?.placeholder && (
        <MessageBar intent="warning">
          <MessageBarBody>
            <strong>Draft:</strong> placeholder wording. Final terms to be supplied before launch.
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={s.terms} tabIndex={0} role="region" aria-label="TELLEC NFR Licence Terms" aria-busy={!terms}>
        {loadFailed && <p className={s.error}>The terms didn't load. Refresh the page to try again.</p>}
        {!terms && !loadFailed && <Spinner size="tiny" label="Loading terms…" />}
        {terms && (
          <>
            <h2 className={s.termsTitle}>{terms.title}</h2>
            <p className={s.meta}>
              Version {terms.version} · effective {terms.effective}
            </p>
            {terms.sections.map((sec) => (
              <section key={sec.heading}>
                <h3 className={s.h3}>{sec.heading}</h3>
                {sec.paragraphs.map((p, i) => (
                  <p key={i} className={s.p}>
                    {p}
                  </p>
                ))}
              </section>
            ))}
          </>
        )}
      </div>

      {/* Same sentence as the landing page, so it's read twice before consenting. */}
      <p className={s.fullControl}>{content.fullControl}</p>

      <div className={s.agree}>
        <Checkbox
          checked={agreed}
          onChange={(_, v) => {
            setAgreed(Boolean(v.checked));
            if (v.checked) setAgreeError('');
          }}
          label="I have read and agree to the TELLEC NFR Licence Terms."
        />
      </div>
      {agreeError && (
        <p className={s.error} role="alert">
          {agreeError}
        </p>
      )}

      <ul className={s.consent} aria-label="About the Microsoft consent">
        <li>
          The consent includes <span className={s.code}>Sites.FullControl.All</span> because Microsoft requires it to read
          site permissions and sharing links.
        </li>
        <li>We read your tenant and don't change anything in it unless you switch on TELLEC Storage.</li>
        <li>You can revoke consent in Microsoft Entra at any time.</li>
      </ul>

      {formError && (
        <MessageBar intent="error">
          <MessageBarBody>{formError}</MessageBarBody>
        </MessageBar>
      )}

      <div className={s.actions}>
        <Button
          appearance="primary"
          size="large"
          className={s.primary}
          disabledFocusable={!agreed || !terms || busy !== null}
          icon={busy === 'consent' ? <Spinner size="tiny" appearance="inverted" /> : undefined}
          onClick={() => go('consent')}
        >
          {busy === 'consent' ? 'Opening Microsoft…' : 'Continue to Microsoft consent'}
        </Button>
        <Button
          appearance="transparent"
          className={s.linkButton}
          disabled={busy !== null}
          onClick={() => go('admin')}
        >
          Not a Global Admin? Send the link to the person who is.
        </Button>
      </div>

      {perms && perms.permissions.length > 0 && (
        <div>
          <Muted>
            Next, a Global Admin approves {perms.permissions.length} Microsoft Graph permissions for TELLEC Docs.
          </Muted>
          <Accordion collapsible>
            <AccordionItem value="perms">
              <AccordionHeader className={s.permsHeader} size="small">
                What each permission is for{perms.placeholder ? ' (draft list)' : ''}
              </AccordionHeader>
              <AccordionPanel>
                <ul className={s.perms}>
                  {perms.permissions.map((p) => (
                    <li key={p.name}>
                      <span className={s.permName}>{p.name}</span>
                      <span className={s.permReason}>{p.reason}</span>
                    </li>
                  ))}
                </ul>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </div>
      )}
    </Shell>
  );
}
